import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useAuth } from '@/context/AuthContext';
import toast from 'react-hot-toast';
import { Volume2, Save, Play } from 'lucide-react';
import { soundNotificationService, SoundType } from '../../services/soundNotificationService';

// Firestore doc that stores which sound plays for each system event.
// Settings rule already restricts writes to Master Admin and reads to active users,
// so anyone signed in will hear the chosen sounds without extra config.
export const SYSTEM_SOUND_DOC = 'settings/notificationSounds';

export interface SystemSoundConfig {
  decisionSound: SoundType;
  entrySound: SoundType;
}

export const DEFAULT_SYSTEM_SOUNDS: SystemSoundConfig = {
  decisionSound: SoundType.Marimba,
  entrySound: SoundType.Harp,
};

// Every available sound the user can pick. Pulled from soundNotificationService.SoundType
// so this list always stays in sync with whatever generators exist.
const SOUND_OPTIONS: { value: SoundType; label: string; description: string }[] = [
  { value: SoundType.Default, label: 'Default', description: 'Classic two-tone notification' },
  { value: SoundType.Chime, label: 'Chime', description: 'Gentle wind-chime cascade' },
  { value: SoundType.Bell, label: 'Bell', description: 'Church bell with harmonics' },
  { value: SoundType.Ding, label: 'Ding', description: 'Quick metallic ding' },
  { value: SoundType.Pop, label: 'Pop', description: 'Sharp bubble pop' },
  { value: SoundType.Bubble, label: 'Bubble', description: 'Rising water bubble' },
  { value: SoundType.Coin, label: 'Coin', description: 'Coin drop with bounces' },
  { value: SoundType.Guitar, label: 'Guitar', description: 'Single guitar pluck' },
  { value: SoundType.Piano, label: 'Piano', description: 'Soft piano key' },
  { value: SoundType.Drums, label: 'Drums', description: 'Snare-drum hit' },
  { value: SoundType.Whistle, label: 'Whistle', description: 'Sharp whistle blow' },
  { value: SoundType.Marimba, label: 'Marimba', description: 'Warm wooden mallet roll' },
  { value: SoundType.Harp, label: 'Harp', description: 'Ascending harp arpeggio' },
  { value: SoundType.Success, label: 'Success', description: 'Ascending major chord' },
];

const SystemNotificationSoundsSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const [config, setConfig] = useState<SystemSoundConfig>(DEFAULT_SYSTEM_SOUNDS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, SYSTEM_SOUND_DOC));
        if (snap.exists()) {
          const data = snap.data() as Partial<SystemSoundConfig>;
          setConfig({
            decisionSound: (data.decisionSound as SoundType) || DEFAULT_SYSTEM_SOUNDS.decisionSound,
            entrySound: (data.entrySound as SoundType) || DEFAULT_SYSTEM_SOUNDS.entrySound,
          });
        }
      } catch (err) {
        console.error('Failed to load system sound config:', err);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, SYSTEM_SOUND_DOC), {
        ...config,
        updatedAt: Timestamp.now(),
        updatedBy: currentUser?.email || 'unknown',
      });
      toast.success('System notification sounds saved');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save (Master Admin only)');
    } finally {
      setSaving(false);
    }
  };

  const preview = (sound: SoundType) => {
    soundNotificationService.playSound(sound).catch(() => { /* user-gesture required on first play */ });
  };

  if (!loaded) {
    return (
      <Card className="bg-gradient-to-br from-slate-900/50 to-gray-900/50 border-gray-700/50 backdrop-blur-sm">
        <div className="p-6 text-center text-gray-400">Loading sound settings...</div>
      </Card>
    );
  }

  const renderRow = (
    label: string,
    helper: string,
    value: SoundType,
    onChange: (v: SoundType) => void,
  ) => (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-200">{label}</h3>
        <span className="text-xs text-gray-500">{helper}</span>
      </div>
      <div className="flex gap-2 items-stretch">
        <select
          value={value}
          onChange={e => onChange(e.target.value as SoundType)}
          className="flex-1 bg-gray-800/60 border border-gray-600/50 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
        >
          {SOUND_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label} — {opt.description}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => preview(value)}
          className="px-3 py-2 text-xs font-medium rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 transition-colors flex items-center gap-1.5"
          title="Preview this sound"
        >
          <Play className="h-3.5 w-3.5" /> Preview
        </button>
      </div>
    </div>
  );

  return (
    <Card className="bg-gradient-to-br from-slate-900/50 to-gray-900/50 border-gray-700/50 backdrop-blur-sm">
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Volume2 className="h-5 w-5 text-amber-400" />
          <h2 className="text-xl font-bold text-white">System Notification Sounds</h2>
        </div>
        <p className="text-sm text-gray-400">
          These sounds play for everyone in the system whenever the corresponding event happens.
          Per-user mute/volume preferences from <span className="text-amber-400">Sound Notifications</span> still apply.
        </p>

        <div className="space-y-5">
          {renderRow(
            'Inventory Decisions',
            'Plays when a recipient marks a milestone item (Melt / Wholesale / Keep / Re-Price / Correct)',
            config.decisionSound,
            v => setConfig(c => ({ ...c, decisionSound: v })),
          )}
          {renderRow(
            'New Product Entries',
            'Plays for reviewers when a new template is submitted for review',
            config.entrySound,
            v => setConfig(c => ({ ...c, entrySound: v })),
          )}
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold px-6 py-2 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Card>
  );
};

export default SystemNotificationSoundsSettings;
